import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType, TableConfig } from "../level_config.js";

const PIECE = { Cube: 1 } as const;

const SUPER_HARD = 2;

interface FormationPoint
{
  u: number;
  v: number;
}

interface FormationSpec
{
  count: number;
  id: string;
  points: FormationPoint[];
  depthScale?: number;
}

// Mirrors the editor's FORMATIONS in fieldLayout.ts exactly (points + depthScale) —
// keep this list in step whenever that one changes, so every shape the generator can
// actually produce stays reachable here for a visual check.
const FORMATIONS: FormationSpec[] = [
  { count: 2, id: "pair-z", points: [ { u: 0, v: 0.2 }, { u: 0, v: -0.3 } ] },
  { count: 2, id: "pair-x", points: [ { u: -0.5, v: 0 }, { u: 0.5, v: 0 } ] },
  { count: 2, id: "staircase", points: [ { u: -0.17, v: 0.17 }, { u: 0.3, v: -0.25 } ] },

  { count: 3, id: "wedge", points: [ { u: -1, v: 1 }, { u: 1, v: 1 }, { u: 0, v: -1 } ], depthScale: 0.75 },
  { count: 3, id: "arrow", points: [ { u: 0, v: 1 }, { u: -1, v: -1 }, { u: 1, v: -1 } ], depthScale: 0.75 },
  { count: 3, id: "staircase", points: [ { u: -1, v: 1 }, { u: 0, v: 0 }, { u: 1, v: -1 } ], depthScale: 0.7 },
  { count: 3, id: "arc", points: [ { u: -1, v: 0.35 }, { u: 0, v: 1 }, { u: 1, v: 0.35 } ], depthScale: 0.8 },
  { count: 3, id: "l-shape", points: [ { u: -0.8, v: 0.6 }, { u: -0.8, v: -0.6 }, { u: 0.6, v: 0 } ], depthScale: 0.7 },
  { count: 3, id: "column-3", points: [ { u: 0, v: 0.3 }, { u: 0, v: 0 }, { u: 0, v: -0.3 } ] },

  { count: 4, id: "diamond", points: [ { u: 0, v: 1 }, { u: -1, v: 0 }, { u: 1, v: 0 }, { u: 0, v: -1 } ], depthScale: 0.9 },
  { count: 4, id: "zigzag", points: [ { u: -0.7, v: 0.8 }, { u: -0.7, v: -0.3 }, { u: 0.7, v: 0.3 }, { u: 0.7, v: -0.8 } ] },
  { count: 4, id: "square", points: [ { u: -0.75, v: 1 }, { u: 0.75, v: 1 }, { u: -0.75, v: -0.5 }, { u: 0.75, v: -1.5 } ], depthScale: 0.8 },
];

const COLS = 5;
const ROWS = 3;
const LAYERS = 2;

function buildLayer( table_index: number ): LevelLayer
{
  const grid: number[][] = Array.from( { length: ROWS }, () => Array( COLS ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let y = 0; y < ROWS; y++ )
    for ( let x = 0; x < COLS; x++ )
    {
      grid[y][x] = id;
      // Variant differs per table so neighbouring tabletops in the same shape
      // read as distinct clumps instead of one continuous mass of cubes.
      pieces.push( { id, type: PIECE.Cube as LevelPieceType, x, y, width: 1, height: 1, variant: table_index % 3 } );
      id++;
    }

  return { grid, pieces };
}

function buildTable( table_index: number, point: FormationPoint, depth_scale: number ): TableConfig
{
  return {
    layers: Array.from( { length: LAYERS }, () => buildLayer( table_index ) ),
    // Exactly what the editor's slotsOf() bakes: the authored anchor with
    // depthScale already folded into v, so resolveFieldLayout reproduces the
    // same shape here as the generator would.
    slot: { u: point.u, v: point.v * depth_scale },
  };
}

function buildConfig( index: number ): LevelConfig
{
  const spec = FORMATIONS[index];
  const depth_scale = spec.depthScale ?? 1;
  const tables = spec.points.slice( 0, spec.count ).map( ( p, i ) => buildTable( i, p, depth_scale ) );

  return {
    level: TableFormationShowcase.BASE_LEVEL + index,
    difficulty: SUPER_HARD as LevelConfig["difficulty"],
    layers: tables[0].layers, // table #1 mirror, same convention as every other multi-table level
    tables,
  };
}

export class TableFormationShowcase
{
  public static readonly BASE_LEVEL = 90300;
  public static readonly COUNT = FORMATIONS.length;

  private static active = false;
  private static index = 0;
  private static configs: ( LevelConfig | null )[] = FORMATIONS.map( () => null );

  public static isActive(): boolean
  {
    return this.active;
  }

  public static isShowcaseLevel( level: number ): boolean
  {
    return level >= this.BASE_LEVEL && level < this.BASE_LEVEL + this.COUNT;
  }

  public static getIndex(): number
  {
    return this.index;
  }

  public static getName( index: number = this.index ): string
  {
    const spec = FORMATIONS[ ( ( index % this.COUNT ) + this.COUNT ) % this.COUNT ];
    return `${spec.count} столи · ${spec.id}`;
  }

  public static currentLevel(): number
  {
    return this.BASE_LEVEL + this.index;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( ( index % this.COUNT ) + this.COUNT ) % this.COUNT;
    return this.currentLevel();
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static beginLevel( level: number ): void
  {
    this.active = true;
    this.index = level - this.BASE_LEVEL;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isShowcaseLevel( level ) )
      return null;

    const index = level - this.BASE_LEVEL;

    if ( this.configs[index] === null )
      this.configs[index] = buildConfig( index );

    return this.configs[index];
  }
}
